import { NavLink } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useIsParentOrAdmin } from '@/hooks/useAuth';
import { cn } from '@/lib/utils';
import api from '@/lib/api';

interface Tab {
  label: string;
  path: string;
  icon: React.ReactNode;
  requiresParentOrAdmin?: boolean;
  testId: string;
  badgeKey?: string; // identifies which badge count to show
}

const tabs: Tab[] = [
  {
    label: 'Calendar',
    path: '/dashboard/calendar',
    testId: 'clickable-tab-calendar',
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
      </svg>
    ),
  },
  {
    label: 'Family',
    path: '/dashboard/family',
    testId: 'clickable-tab-family',
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
      </svg>
    ),
  },
  {
    label: "Kai's Care",
    path: '/dashboard/care',
    testId: 'clickable-tab-care',
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
      </svg>
    ),
  },
  {
    label: 'Schedule',
    path: '/dashboard/schedule',
    testId: 'clickable-tab-schedule',
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
      </svg>
    ),
  },
  {
    label: 'Tasks',
    path: '/dashboard/tasks',
    testId: 'clickable-tab-tasks',
    badgeKey: 'tasks',
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
      </svg>
    ),
  },
  {
    label: 'Dashboard',
    path: '/dashboard/overview',
    testId: 'clickable-tab-dashboard',
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zm10 0a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zm10 0a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
      </svg>
    ),
    requiresParentOrAdmin: true,
  },
];

export function TabBar() {
  const isParentOrAdmin = useIsParentOrAdmin();

  // Fetch open task count for badge
  const { data: taskCounts } = useQuery({
    queryKey: ['shared-tasks-counts'],
    queryFn: async () => {
      const res = await api.get('/shared-tasks/counts');
      return res.data.counts as Record<string, number>;
    },
    staleTime: 1000 * 30,
    refetchInterval: 30000,
  });

  const openTaskCount = (taskCounts?.todo || 0) + (taskCounts?.in_progress || 0);

  const visibleTabs = tabs.filter(
    (tab) => !tab.requiresParentOrAdmin || isParentOrAdmin
  );

  return (
    <nav data-testid="container-tabbar" className="border-b border-slate-700 bg-card">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center space-x-1 overflow-x-auto">
          {visibleTabs.map((tab) => (
            <NavLink
              key={tab.path}
              to={tab.path}
              data-testid={tab.testId}
              className={({ isActive }) =>
                cn(
                  'flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap relative',
                  isActive
                    ? 'text-white border-accent'
                    : 'text-gray-400 border-transparent hover:text-white hover:border-slate-600'
                )
              }
            >
              <span className="relative">
                {tab.icon}
                {tab.badgeKey === 'tasks' && openTaskCount > 0 && (
                  <span className="absolute -top-1.5 -right-2 min-w-[16px] h-4 px-1 text-[9px] font-bold bg-blue-500 text-white rounded-full flex items-center justify-center">
                    {openTaskCount > 99 ? '99+' : openTaskCount}
                  </span>
                )}
              </span>
              <span>{tab.label}</span>
            </NavLink>
          ))}
        </div>
      </div>
    </nav>
  );
}

export default TabBar;
